import React, { useEffect, useMemo, useRef, useState } from "react";
import { useRootDispatch, useRootSelector } from "~redux/hooks";
import { aircraftIsAselSelector, openView, showSatCommIndicatorSelector } from "~redux/slices/edstSlice";
import { aclPostingModeSelector, toolsOptionsSelector } from "~redux/slices/aclSlice";
import {
    cpdlcSessionSelector,
    flightplanSelector,
    sectorTrackSelector,
    trackCoordinationDataSelector,
    trackFromFpIdSelector,
} from "~redux/slices/aircraftSlice";
import { eramHubConnection } from "~/eramHubConnection";
import { processEramMessage, updateEdstFpData } from "~redux/thunks/processEramMessage";
import type { ColorSource, Container, FederatedEventHandler, FederatedPointerEvent } from "pixi.js";
import { aircraftSelect } from "~redux/thunks/aircraftSelect";
import {
    alertOrange,
    alertRed,
    alertYellow,
    cpdlcFont2,
    cpdlcFont2Dimension,
    defaultTint,
    edstFontName,
    edstFontDim,
    mutedAlertRed,
    mutedAlertYellow,
    unpairedFpTint,
} from "~/utils/constants";
import {
    edstFpSelector,
    fpTrackPositionSelector,
    fpTrajectorySelector,
    mtcaConflictsForFpIdSelector,
} from "~redux/slices/edstFlightplanSlice";
import { TableField } from "components/utils/TableField";
import {
    artccIdSelector,
    colorNameMap,
    cpdlcMiniMOView,
    cpdlcTocHeldView,
    eramCpdlcFontNameMap,
    EramInput,
    getBitmapTextStyles,
    ownedFavIdsSelector,
    sectorIdSelector,
    TBE,
    TBH,
    TBP,
    useFocused,
    useInputProps,
    useStableCallback,
} from "@poscon/shared-frontend";
import { useTableColumnPositions } from "contexts/TableColumnPositionContext";
import { EramCpdlcSession } from "@poscon/eram-cpdlc";
import { Rectangle } from "pixi.js";
import {
    FlightplanId,
    VCI_SYMBOL,
    TOC_COMPLETE_SYMBOL,
    TrackId,
    formatRoute,
    getMtcaLevels,
    formatBeaconCode,
} from "@poscon/shared-types";
import {
    EramCommunicationStatus,
    EramFlightplan,
    EdstFpData,
    EramSectorTrack,
    AclRowField,
    stringToParsedTokenArray,
    formatNasAltitude,
    getFpInSectorTime,
    Coordinate,
} from "@poscon/shared-types/eram";
import { SectorId } from "@poscon/shared-types/poscon";
import { openMenuThunk } from "~/redux/thunks/openMenuThunk";
import { EdstAselMenu } from "~/types/edstView";
import { useTimeContext } from "~/contexts/timeContext";

type AdditionalFields =
    | "VCI"
    | "ALERT_RED"
    | "ALERT_ORANGE"
    | "ALERT_YELLOW"
    | "REMARKS_TOGGLE_BOX"
    | "HOLD_INDICATOR";

type AclRowProps = {
    fpId: FlightplanId;
};

const vciTextMap: Record<NonNullable<EramCommunicationStatus>, string> = {
    ON_FREQ: VCI_SYMBOL,
    TOC: TOC_COMPLETE_SYMBOL,
};

export const AclRow = ({ fpId }: AclRowProps) => {
    const fp = useRootSelector((state) => flightplanSelector(state, fpId));
    const edstFp = useRootSelector((state) => edstFpSelector(state, fpId));
    const track = useRootSelector((state) => trackFromFpIdSelector(state, fpId));
    const sectorTrack = useRootSelector((state) => (track ? sectorTrackSelector(state, track.id) : null));
    const cpdlcSession = useRootSelector((state) => cpdlcSessionSelector(state, fpId));

    return fp && edstFp ? (
        <AclRowActual
            trackId={track?.id}
            owner={track?.owner}
            ownerShort={track?.ownerShort}
            sectorTrack={sectorTrack}
            fp={fp}
            edstFp={edstFp}
            cpdlcSession={cpdlcSession}
        />
    ) : null;
};

type FlidColProps = {
    x?: number;
    y?: number;
    width: number;
    height: number;
    tint: ColorSource;
    selected?: boolean;
    owner?: SectorId | null;
    ownerShort?: string | null;
    fp: EramFlightplan;
    sectorTrack?: EramSectorTrack | null;
    edstFp: EdstFpData;
    cpdlcSession?: EramCpdlcSession;
    onmousedown: (e: FederatedPointerEvent, ref?: Container, openMenu?: EdstAselMenu) => void;
};
const FlidCol = ({
    x = 0,
    y = 0,
    width,
    height,
    onmousedown,
    tint,
    selected,
    owner,
    ownerShort,
    sectorTrack,
    fp,
    edstFp,
    cpdlcSession,
}: FlidColProps) => {
    const sectorId = useRootSelector(sectorIdSelector);
    const ref = useRef<Container>(null);
    const focused = useFocused(ref);
    const showSatCommIndicator = useRootSelector(showSatCommIndicatorSelector);

    const tocPrefix = cpdlcSession ? EramCpdlcSession.getFieldATocPrefix(cpdlcSession, sectorId) : null;
    const suffix = cpdlcSession ? EramCpdlcSession.getFieldASuffix(cpdlcSession, sectorId) : null;
    const genericPrefix = cpdlcSession
        ? EramCpdlcSession.getDialogStatusText(cpdlcSession, sectorTrack?.cpdlcFieldDialogIdMap.genericUplink)
        : null;

    const prefix = `${tocPrefix ?? ""}${genericPrefix ?? ""}`;
    const callsignText = `${fp.callsign}${showSatCommIndicator && EramFlightplan.isSatCommEquipped(fp) ? "*" : ""}`;
    const ownerText = !owner ? "(UNK)" : owner === sectorId ? "" : `(${ownerShort ?? "UNK"})`;

    const suffixX = edstFontDim.width * (3 + callsignText.length) + cpdlcFont2Dimension.width * 2 + 6;

    return (
        <container x={x} y={y} sortableChildren ref={ref}>
            <graphics
                eventMode="static"
                hitArea={new Rectangle(0, 0, width, height)}
                onMouseDown={onmousedown}
                draw={(graphics) => {
                    graphics.clear();

                    graphics
                        .rect(0, 0, width, height - 1)
                        .fill({ color: selected ? tint : 0, alpha: selected ? 1 : 0 });
                    if (focused) {
                        graphics.rect(0, 0, width, height - 1).stroke({ width: 1, color: 0xf0f0f0, alignment: 0 });
                    }
                    graphics.setStrokeStyle({});
                    if (selected) {
                        const fillColor = selected ? tint : 0x000000;
                        graphics.rect(0, 0, width - 1, height - 1).fill(fillColor);
                        if (prefix) {
                            graphics
                                .rect(
                                    edstFontDim.width * 3 + cpdlcFont2Dimension.width * (2 - prefix.length) - 1,
                                    0,
                                    cpdlcFont2Dimension.width * prefix.length + 2,
                                    height - 1
                                )
                                .fill(edstFp.highlight ? 0x414141 : 0x000000);
                        }
                        if (suffix) {
                            graphics
                                .rect(
                                    suffixX - 1,
                                    0,
                                    cpdlcFont2Dimension.width * suffix.length + 2,
                                    height - 1
                                )
                                .fill(edstFp.highlight ? 0x414141 : 0x000000);
                        }
                    }
                }}
            />
            {prefix && (
                <bitmapText
                    eventMode="static"
                    label="FIELD_A_PREFIX"
                    x={edstFontDim.width * 3 + cpdlcFont2Dimension.width * (2 - prefix.length)}
                    y={2}
                    text={prefix}
                    onMouseDown={(e: FederatedPointerEvent) => onmousedown(e, ref.current!, cpdlcMiniMOView)}
                    style={{ ...getBitmapTextStyles(cpdlcFont2), fill: colorNameMap.white }}
                    zIndex={2}
                />
            )}
            <bitmapText
                eventMode="none"
                text={fp.cid ?? ""}
                x={2}
                y={2}
                style={{ ...getBitmapTextStyles(edstFontName), fill: selected ? 0 : tint }}
            />
            <bitmapText
                eventMode="none"
                text={callsignText}
                x={edstFontDim.width * 3 + cpdlcFont2Dimension.width * 2 + 2}
                y={2}
                style={{ ...getBitmapTextStyles(edstFontName), fill: selected ? 0 : tint }}
            />
            {suffix && (
                <bitmapText
                    eventMode="static"
                    label="FIELD_A_SUFFIX"
                    x={suffixX}
                    y={2}
                    text={suffix}
                    onMouseDown={(e: FederatedPointerEvent) => {
                        onmousedown(e, ref.current!, cpdlcTocHeldView);
                    }}
                    style={{ ...getBitmapTextStyles(cpdlcFont2), fill: colorNameMap.white }}
                    zIndex={2}
                />
            )}
            {ownerText && (
                <bitmapText
                    eventMode="none"
                    text={ownerText}
                    x={edstFontDim.width * (3 + callsignText.length) + cpdlcFont2Dimension.width * (2 + (suffix?.length ?? 0)) + 6}
                    y={2}
                    style={{ ...getBitmapTextStyles(edstFontName), fill: selected ? 0 : tint }}
                />
            )}
        </container>
    );
};

type AclRowActualProps = {
    trackId?: TrackId;
    owner?: SectorId | null;
    ownerShort?: string | null;
    sectorTrack: EramSectorTrack | null;
    fp: EramFlightplan;
    edstFp: EdstFpData;
    cpdlcSession?: EramCpdlcSession;
};

/**
 * Single ACL row
 */
const AclRowActual = ({
    fp,
    edstFp,
    trackId,
    owner,
    ownerShort,
    sectorTrack,
    cpdlcSession,
}: AclRowActualProps) => {
    const dispatch = useRootDispatch();
    const artccId = useRootSelector(artccIdSelector);
    const ownedFavIds = useRootSelector(ownedFavIdsSelector);
    const now = useTimeContext().getTime();

    const asel = useRootSelector((state) => aircraftIsAselSelector(state, fp.id));
    const manualPosting = useRootSelector(aclPostingModeSelector);
    const toolOptions = useRootSelector(toolsOptionsSelector);
    const { positions, widths, hiddenColumns } = useTableColumnPositions<AclRowField | AdditionalFields>();
    const coordination = useRootSelector((state) =>
        trackId ? trackCoordinationDataSelector(state, trackId) : null
    );
    const [displayScratchHdg, setDisplayScratchHdg] = useState(false);
    const [displayScratchSpd, setDisplayScratchSpd] = useState(false);

    const fpId = fp.id;
    const trajectory = useRootSelector((state) => fpTrajectorySelector(state, fpId));
    const mtcaConflicts = useRootSelector((state) => mtcaConflictsForFpIdSelector(state, fpId));
    const fpTrack = useRootSelector((state) => fpTrackPositionSelector(state, fpId));

    const times =
        trajectory?.favCrossings && artccId
            ? getFpInSectorTime(trajectory.favCrossings, artccId, ownedFavIds)
            : { minTime: 0, maxTime: 0 };

    const { level1Conflicts = [], level2Conflicts = [] } = useMemo(() => {
        let conflicts = mtcaConflicts.filter((conflict) => conflict.affectedFavs.some((favId) => ownedFavIds.includes(favId)));
        return fpTrack
            ? getMtcaLevels(conflicts, Math.max(times.minTime, now), times.maxTime)
            : { level1Conflicts: [], level2Conflicts: [] };
    }, [ownedFavIds, fpTrack, mtcaConflicts, times.minTime, times.maxTime]);

    const clearedAlt =
        coordination?.localInterimAltitude ??
        coordination?.interimAltitude?.altitude ??
        fp.assignedAltitude?.simple;

    const level1Tint =
        clearedAlt &&
            level1Conflicts.some((conflict) =>
                [conflict.conflictA, conflict.conflictB].some(
                    (c) =>
                        c.fpId === fpId &&
                        clearedAlt <= c.segment.maxAltitude + 500 && c.segment.minAltitude - 500 <= clearedAlt
                )
            )
            ? alertRed
            : mutedAlertRed;
    const level2Tint =
        clearedAlt &&
            level2Conflicts.some((conflict) =>
                [conflict.conflictA, conflict.conflictB].some(
                    (c) =>
                        c.fpId === fpId &&
                        clearedAlt <= c.segment.maxAltitude + 500 && c.segment.minAltitude - 500 <= clearedAlt
                )
            )
            ? alertYellow
            : mutedAlertYellow;

    const formattedRoute = formatRoute(fp.route, fp.departure, fp.destination, fp.routeIsTruncated);
    // coral box indicates that aircraft is assigned an altitude in RVSM airspace but equipment says it is not RVSM approved
    const showCoralBox = !!(
        !EramFlightplan.isRvsmEquipped(fp) &&
        fp.assignedAltitude?.simple &&
        fp.assignedAltitude.simple > 280 &&
        toolOptions.nonRvsmIndicator
    );

    const isSelected = (field: AclRowField) => {
        return asel?.fpId === fpId && asel?.field === field;
    };

    const handleMouseDown = useStableCallback(
        (element: Container, field: AclRowField, menu?: EdstAselMenu, offset?: Coordinate) => {
            if (menu) {
                dispatch(openMenuThunk(menu, element, offset));
            }
            dispatch(aircraftSelect(fpId, field, menu));
        }
    );

    const handleRouteClick = (element: Container) => {
        if (edstFp.routeDisplay === "HOLD_ANNOTATIONS_DISPLAY_OPTION") {
            handleMouseDown(element, "ROUTE_ACL_ROW_FIELD", "HOLD_MENU");
        } else {
            handleMouseDown(element, "ROUTE_ACL_ROW_FIELD", "ROUTE_MENU");
        }
    };

    const handleHotboxMouseDown: FederatedEventHandler = (event) => {
        switch (event.button) {
            case TBP:
                dispatch(
                    updateEdstFpData(fpId, {
                        showFreeText: !edstFp.showFreeText,
                    })
                );
                break;
            case TBE:
                dispatch(updateEdstFpData(fpId, { spa: !edstFp.spa }));
                break;
            case TBH:
                dispatch(
                    updateEdstFpData(fpId, {
                        highlight: !edstFp.highlight,
                    })
                );
                break;
            default:
                break;
        }
    };

    const toggleVci = () => {
        if (!edstFp?.acknowledged) {
            dispatch(updateEdstFpData(fpId, { acknowledged: true }));
        } else {
            dispatch(processEramMessage(stringToParsedTokenArray(`//${fp.cid ?? fp.callsign}`)));
        }
    };

    const handleHoldClick: FederatedEventHandler = (event) => {
        switch (event.button) {
            case TBP:
                if (!coordination?.holdAnnotations) {
                    handleMouseDown(event.target as Container, "ROUTE_ACL_ROW_FIELD", "HOLD_MENU");
                } else {
                    dispatch(
                        updateEdstFpData(fpId, {
                            routeDisplay: !edstFp.routeDisplay ? "HOLD_ANNOTATIONS_DISPLAY_OPTION" : null,
                        })
                    );
                }
                break;
            case TBE:
                handleMouseDown(event.target as Container, "ROUTE_ACL_ROW_FIELD", "HOLD_MENU");
                break;
            case TBH:
                if (coordination?.holdAnnotations) {
                    handleMouseDown(event.target as Container, "ROUTE_ACL_ROW_FIELD", "CANCEL_HOLD_MENU");
                }
                break;
            default:
                break;
        }
    };

    const handleRemarksClick: FederatedEventHandler = (event) => {
        if (!sectorTrack?.communicationStatus && !manualPosting) {
            dispatch(updateEdstFpData(fpId, { acknowledged: true }));
        }
        switch (event.button) {
            case TBP:
                dispatch(
                    updateEdstFpData(fpId, {
                        routeDisplay:
                            edstFp.routeDisplay !== "REMARKS_DISPLAY_OPTION" && fp.remarks?.length
                                ? "REMARKS_DISPLAY_OPTION"
                                : null,
                        remarksChecked: true,
                    })
                );
                break;
            default:
                break;
        }
    };

    const handleFlidClick = (event: FederatedPointerEvent, container?: Container, menu?: EdstAselMenu) => {
        switch (event.button) {
            case TBH:
                if (edstFp.pendingRemoval) {
                    eramHubConnection.emit("deleteEdstFp", fpId);
                }
                break;
            case TBE: {
                dispatch(
                    updateEdstFpData(fpId, { showConflicts: edstFp.showConflicts === "sector" ? null : "sector" })
                );
                dispatch(openView("GPD"));
                break;
            }
            default:
                if (!manualPosting && event.detail === 2 && !edstFp.acknowledged) {
                    dispatch(updateEdstFpData(fpId, { acknowledged: true }));
                }
                handleMouseDown(container ?? event.currentTarget, "FID_ACL_ROW_FIELD", menu, [flidWidth + 2, 0]);
                break;
        }
    };

    const handleHeadingClick: FederatedEventHandler = (event) => {
        event.preventDefault();
        if (coordination) {
            switch (event.button) {
                case TBP:
                    handleMouseDown(event.target as Container, "HDG_ACL_ROW_FIELD", "HEADING_MENU");
                    break;
                case TBE:
                    if (edstFp.scratchHdg && (displayScratchHdg || !coordination.heading)) {
                        // const promotedHdg = "LRH".includes(entry.scratchpadHeading.slice(-1)) ? entry.scratchpadHeading : `H${entry.scratchpadHeading}`;
                    }
                    break;
                case TBH:
                    if (edstFp.scratchHdg && (displayScratchHdg || !coordination.heading)) {
                        dispatch(updateEdstFpData(fpId, { scratchHdg: null }));
                    } else if (coordination.heading) {
                        dispatch(processEramMessage(stringToParsedTokenArray(`QS */ ${fp.cid}`)));
                    }
                    break;
                default:
                    break;
            }
        }
    };

    const handleSpeedClick: FederatedEventHandler = (event) => {
        event.preventDefault();
        if (coordination) {
            switch (event.button) {
                case TBP:
                    handleMouseDown(event.target as Container, "SPD_ACL_ROW_FIELD", "SPEED_MENU");
                    break;
                case TBE:
                    if (edstFp.scratchSpd && (displayScratchSpd || !coordination.speed)) {
                        // const promotedSpd = entry.scratchpadSpeed.slice(0, 1) === "M" ? entry.scratchpadSpeed : `S${entry.scratchpadSpeed}`;
                    }
                    break;
                case TBH:
                    if (edstFp.scratchSpd && (displayScratchSpd || !coordination.speed)) {
                        dispatch(updateEdstFpData(fpId, { scratchSpd: null }));
                    } else if (coordination.speed) {
                        dispatch(processEramMessage(stringToParsedTokenArray(`QS /* ${fp.cid}`)));
                    }
                    break;
                default:
                    break;
            }
        }
    };

    const {
        VCI: vciX,
        ALERT_RED: alertRedX,
        ALERT_YELLOW: alertYellowX,
        ALERT_ORANGE: alertOrangeX,
        FID_ACL_ROW_FIELD: flidX,
        PA_ACL_ROW_FIELD: paX,
        TYPE_ACL_ROW_FIELD: typeX,
        ALT_ACL_ROW_FIELD: altX,
        CODE_ACL_ROW_FIELD: codeX,
        HDG_ACL_ROW_FIELD: hdgX,
        SPD_ACL_ROW_FIELD: spdX,
        REMARKS_TOGGLE_BOX: remarksToggleBoxX,
        ROUTE_ACL_ROW_FIELD: routeX,
    } = positions;
    const spaIndicatorX = paX + widths.PA_ACL_ROW_FIELD + 2;
    const hotboxX = spaIndicatorX + edstFontDim.width + 6;

    const { FID_ACL_ROW_FIELD: flidWidth, HDG_ACL_ROW_FIELD: hdgWidth } = widths;

    let tint: ColorSource = !trackId ? unpairedFpTint : defaultTint;
    if (trackId && edstFp.pendingRemoval) {
        tint = colorNameMap.mediumGrey;
    }

    // TODO: format hold text
    const routeDisplayText =
        edstFp.routeDisplay === "HOLD_ANNOTATIONS_DISPLAY_OPTION"
            ? "HOLD"
            : edstFp.routeDisplay === "REMARKS_DISPLAY_OPTION"
                ? fp.remarks
                : formattedRoute;

    const routeWidth = Math.min(widths.ROUTE_ACL_ROW_FIELD, edstFontDim.width * routeDisplayText.length + 4);

    let altText = "";
    if (fp.assignedAltitude?.simple) {
        if (coordination?.localInterimAltitude) {
            altText = `${coordination.localInterimAltitude.toString()}L`;
        } else if (coordination?.interimAltitude) {
            altText = `${coordination.interimAltitude.altitude.toString()}${coordination.interimAltitude.type}`;
        }
        altText += fp.assignedAltitude.simple.toString();
    } else if (fp.assignedAltitude) {
        altText = formatNasAltitude(fp.assignedAltitude);
    }

    const { setInputValue: setFreeformTextInputValue, ...inputProps } = useInputProps(
        `ACL/table/row/${fpId}/freeformtext`,
        edstFp?.freeformText ?? "",
        (value) => {
            dispatch(
                updateEdstFpData(fpId, {
                    freeformText: value,
                })
            );
            inputProps.setInputFocus(false);
        },
        {
            initialShowInput: false,
            focusOnMount: false,
        }
    );

    useEffect(() => {
        if (edstFp.freeformText) {
            setFreeformTextInputValue(edstFp.freeformText);
        }
    }, [edstFp.freeformText, setFreeformTextInputValue]);

    useEffect(() => {
        if (!edstFp.showFreeText && edstFp.freeformText !== inputProps.value) {
            dispatch(
                updateEdstFpData(fpId, {
                    freeformText: inputProps.value,
                })
            );
            inputProps.setInputFocus(false);
        }
    }, [edstFp.freeformText, edstFp.showFreeText, fpId, inputProps.value, inputProps.setInputFocus]);


    // TODO: include CPDLC fields

    const highlightWidth = Math.max(
        routeX + routeWidth - flidX + 1,
        typeX - flidX + edstFontDim.width * inputProps.value.length
    );
    const freeTextWidth = highlightWidth + (flidX - typeX) - 2;

    return (
        <container sortableChildren>
            {edstFp.highlight && (
                <graphics
                    eventMode="none"
                    zIndex={0}
                    draw={(g) => {
                        g.clear();
                        g.rect(
                            flidX - 1,
                            -1,
                            highlightWidth,
                            edstFp.showFreeText ? edstFontDim.height * 2 + 9 : edstFontDim.height + 3
                        ).fill(0x414141);
                    }}
                />
            )}
            <container zIndex={1}>
                <TableField
                    key="VCI"
                    x={vciX}
                    fontName={sectorTrack?.communicationStatus ? eramCpdlcFontNameMap[2] : edstFontName}
                    text={
                        sectorTrack?.communicationStatus
                            ? vciTextMap[sectorTrack.communicationStatus]
                            : !edstFp?.acknowledged
                                ? "N"
                                : " "
                    }
                    tint={sectorTrack?.communicationStatus ? colorNameMap.green : colorNameMap.grey}
                    hoverTint={sectorTrack?.communicationStatus ? colorNameMap.green : colorNameMap.white}
                    onmousedown={toggleVci}
                />
                <TableField
                    key="ALERT_RED"
                    x={alertRedX}
                    text={level1Conflicts.length > 0 ? Math.min(level1Conflicts.length, 9).toString() : " "}
                    disabled
                    tint={level1Tint}
                    borderTint={level1Conflicts.length > 0 ? level1Tint : 0xadadad}
                />
                <TableField
                    key="ALERT_YELLOW"
                    x={alertYellowX}
                    text={level2Conflicts.length > 0 ? Math.min(level2Conflicts.length, 9).toString() : " "}
                    disabled
                    tint={level2Tint}
                    borderTint={level2Conflicts.length > 0 ? level2Tint : 0xadadad}
                />
                <TableField key="ALERT_ORANGE" x={alertOrangeX} text=" " disabled borderTint={0xadadad} />
                <FlidCol
                    x={flidX}
                    width={flidWidth}
                    height={edstFontDim.height + 2}
                    onmousedown={handleFlidClick}
                    selected={isSelected("FID_ACL_ROW_FIELD")}
                    tint={tint}
                    fp={fp}
                    sectorTrack={sectorTrack}
                    edstFp={edstFp}
                    cpdlcSession={cpdlcSession}
                    owner={owner}
                    ownerShort={ownerShort}
                />
                <TableField key="PO" x={paX} tint={tint} text="" disabled />
                {edstFp.spa && (
                    <TableField key="SPA_INDICATOR" x={spaIndicatorX} text="^" borderTint={0x575757} disabled />
                )}
                <TableField
                    key="HOTBOX"
                    x={hotboxX}
                    text={edstFp.freeformText ? "*" : " "}
                    borderTint={0x575757}
                    onmousedown={handleHotboxMouseDown}
                />
                <TableField
                    key="TYPE"
                    tint={tint}
                    x={typeX}
                    text={
                        hiddenColumns.includes("TYPE_ACL_ROW_FIELD") ? "" : `${fp.aircraftType}/${fp.equipmentQualifier}`
                    }
                    onmousedown={(event) => handleMouseDown(event.currentTarget as Container, "TYPE_ACL_ROW_FIELD")}
                    selected={isSelected("TYPE_ACL_ROW_FIELD")}
                    disabled={hiddenColumns.includes("TYPE_ACL_ROW_FIELD")}
                />
                <TableField
                    key="ALT"
                    tint={tint}
                    x={altX}
                    text={altText}
                    selected={isSelected("ALT_ACL_ROW_FIELD")}
                    onmousedown={(event) =>
                        handleMouseDown(event.currentTarget as Container, "ALT_ACL_ROW_FIELD", "ALTITUDE_MENU")
                    }
                />
                <TableField
                    key="CODE"
                    tint={tint}
                    x={codeX}
                    text={
                        hiddenColumns.includes("CODE_ACL_ROW_FIELD") || !fp.squawk
                            ? ""
                            : ` ${formatBeaconCode(fp.squawk)}`
                    }
                    selected={isSelected("CODE_ACL_ROW_FIELD")}
                    onmousedown={(event) => handleMouseDown(event.currentTarget as Container, "CODE_ACL_ROW_FIELD")}
                    disabled={hiddenColumns.includes("CODE_ACL_ROW_FIELD")}
                />
                <TableField
                    key="HDG"
                    tint={tint}
                    x={hdgX}
                    text={hiddenColumns.includes("HDG_ACL_ROW_FIELD") ? "" : (coordination?.heading ?? "")}
                    disabled={hiddenColumns.includes("HDG_ACL_ROW_FIELD")}
                    textX={2 + edstFontDim.width * Math.max(0, 4 - (coordination?.heading?.length ?? 0))}
                    selected={isSelected("HDG_ACL_ROW_FIELD")}
                    width={3 + edstFontDim.width * (hiddenColumns.includes("HDG_ACL_ROW_FIELD") ? 2 : 4)}
                    onmousedown={handleHeadingClick}
                />
                <TableField key="HDG_SPD_SLASH" x={hdgX + hdgWidth + 2} text="/" disabled />
                <TableField
                    key="SPD"
                    tint={tint}
                    x={spdX}
                    text={hiddenColumns.includes("SPD_ACL_ROW_FIELD") ? "" : (coordination?.speed ?? "")}
                    disabled={hiddenColumns.includes("SPD_ACL_ROW_FIELD")}
                    textX={2}
                    selected={isSelected("SPD_ACL_ROW_FIELD")}
                    width={3 + edstFontDim.width * (hiddenColumns.includes("SPD_ACL_ROW_FIELD") ? 2 : 4)}
                    onmousedown={handleSpeedClick}
                />
                <TableField
                    key="REMARKS_TOGGLE_BOX"
                    x={remarksToggleBoxX}
                    text={fp.remarks ? "*" : " "}
                    onmousedown={handleRemarksClick}
                />
                <TableField
                    key="ROUTE"
                    tint={tint}
                    x={routeX}
                    textX={2}
                    text={routeDisplayText}
                    selected={isSelected("ROUTE_ACL_ROW_FIELD")}
                    maxWidth={routeWidth}
                    onmousedown={(event) => handleRouteClick(event.currentTarget as Container)}
                />
            </container>
            {edstFp.showFreeText && (
                <EramInput
                    {...inputProps}
                    zIndex={1}
                    x={typeX}
                    y={edstFontDim.height + 6}
                    width={freeTextWidth}
                    tint={tint}
                    highlightColor={defaultTint}
                />
            )}
        </container>
    );
};
